
import {
  formatByCountry,
  formatInputByCountry,
  formatInputMoney,
  formatInputMoneyWithDecimal,
  formatPriceRP,
  parseByCountry,
  replaceComma
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { Vue, Options, prop } from "vue-class-component";
import { isCustomerApps } from "@/app/ui/views/track-delivery/delivery-tab/modules";
import {
  parseMultiFieldZodError,
  parseZodError,
  validationCurrentIndex
} from "../../../utils";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import {
  BookingV3,
  ConfigValidationForm,
  DestinationCity
} from "@/domain/entities/CreateBookingV3";
import {
  estimationPriceSchema,
  getMinMaxGoodsPriceFromFlag
} from "./validation";
import { Insurance } from "@/domain/entities/Insurance";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { CodConfigurationController } from "@/app/ui/controllers/CodConfigurationController";
import { PropType } from "vue";
import { InsuranceTypeDeliveryData } from "@/domain/entities/Booking";
import { LocationController } from "@/app/ui/controllers/LocationController";
import {
  configPrice,
  getCurrencyCodeByCountry
} from "@/app/infrastructures/misc/common-library/modules/Price";
import Checkbox from "primevue/checkbox";
import { ExchangeRateController } from "@/app/ui/controllers/ExchangeRateController";
import { ExchangeRateData } from "@/domain/entities/ExchangeRate";
import {
  CodInternationalCountry,
  DetailCodConfigurationRetail
} from "@/domain/entities/CodConfiguration";
import { ApiRequestList } from "@/domain/entities/MainApp";

class Props {
  form = prop<BookingV3>({
    required: true
  });
  formType = prop<string>({
    default: "",
    type: String
  });
  listCodActive = prop<any>({
    required: true
  });
  listDfodActive = prop<any>({
    required: true
  });
  currentIndex = prop<number>({
    required: true
  });
  goodsPriceIndex = prop<number>({
    required: true
  });
  insuranceData = prop<Insurance>({
    required: true
  });
  configGoodsPrice = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configInsurance = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configCodAmount = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  isRebookingFlow = prop<boolean>({
    default: false
  });
  isEditSttMY = prop<boolean>({
    default: false
  });
  isBookingShipment = prop<boolean>({
    default: false
  });
  bookingType = prop<string>({
    default: "",
    type: String
  });
  schemaGoodsPriceType = prop<"retail" | "client" | "shipment_ca">({
    default: "retail",
    type: String as PropType<"retail" | "client" | "shipment_ca">
  });
  excludeEstimationValidation = prop<boolean>({
    default: false
  });
  insuranceBasic = prop<InsuranceTypeDeliveryData>({
    default: () => new InsuranceTypeDeliveryData(),
    type: Object
  });
  isRetail = prop<boolean>({
    default: false,
    type: Boolean
  });
  isForceShowInsurance = prop<boolean>({
    default: true,
    type: Boolean
  });
  destination = prop<DestinationCity>({
    default: () => new DestinationCity(),
    type: Object
  });
  isEdit = prop<boolean>({
    default: true,
    type: Boolean
  });
  // Mirrors edit-booking.vue's own `isFirstRender` flag (see its
  // getUpsellingData guard: `if (!this.destinationCity.id || this.isFirstRender) return;`).
  // Parents that bulk-hydrate `form` from a saved STT (edit flow) pass this
  // in as true until hydration settles, so the currency-reset watcher below
  // doesn't mistake "form just got hydrated" for "user changed destination"
  // and wipe the just-loaded price. Parents with no hydration step (fresh
  // create forms) simply never set it, so the watcher behaves normally.
  isFirstRender = prop<boolean>({
    default: false,
    type: Boolean
  });
  isCrossCountryRoute = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ["update:form", "update,currentIndex", "reset-insurance-type"],
  components: {
    Checkbox
  },
  watch: {
    "formData.isCOD": {
      handler: function() {
        this.onChangeDeliveryFeeType();
        this.onValidateEstimationPrice(this.formData.estimationPrice);
      },
      deep: true,
      immediate: true
    },
    "formData.isDFOD": {
      handler: function() {
        this.onChangeDeliveryFeeType();
        this.onValidateEstimationPrice(this.formData.estimationPrice);
      },
      immediate: true,
      deep: true
    },
    "formData.productName": function() {
      this.onChangeDeliveryFeeType(true);
    },
    "insuranceBasic.isAvailable": function(
      newValue: boolean,
      oldValue: boolean
    ) {
      if (
        oldValue &&
        !newValue &&
        (this.formData?.estimationPrice || 0) < 5000000
      ) {
        this.$emit("reset-insurance-type");
      }
    },
    effectiveCurrencyCode: function(this: any) {
      // Destination country changed, or COD/DFOD mode crossed a currency
      // boundary — Harga Barang & Tagihan COD are cleared so the user
      // re-enters a value in the now-correct currency. Detail Barang
      // (commodity) is a separate component/currency and is unaffected.
      // Skip while the parent is still bulk-hydrating `form` (edit flow) —
      // otherwise this would wipe a price that was just loaded from the
      // saved STT, not one the user is actively editing.
      if (this.isFirstRender) return;
      this.formData.estimationPrice = "";
      this.formData.codAmount = "";
      this.errMsgPrice = "";
      this.errorMandatory = false;
    },
    isCrossCountryCod() {
      this.getExchangeRate();
      this.automaticSetCheckbox(this.formData.estimationPrice);
    },
    "form.destinationCountry.code": {
      handler: function() {
        this.getCodConfig();
        if (this.isEdit)
          this.onValidateEstimationPrice(this.formData.estimationPrice);
      },
      deep: true
    }
  }
})
class ProductPriceInsurance extends Vue.with(Props) {
  errMsgPrice = "";
  disabledInsuranceBasic = false;
  shippingIsProtected = false;
  errorMandatory = false;

  mounted() {
    if (this.isBookingShipment) {
      this.onChangeDeliveryFeeType();
      this.onValidateEstimationPrice(this.formData.estimationPrice);
    }
  }

  get getRulePriceDisabled() {
    return this.configGoodsPrice.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get getRuleInsuranceDisabled() {
    return this.configInsurance.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get getRuleCodDisabled() {
    return this.configCodAmount.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get accountIsNonForeign() {
    return !AccountController.accountData.accountIsForeign && this.isClientID;
  }

  get isClientID() {
    const clientCountry = this.formData.clientName.clientCountry;
    if (!clientCountry) return true;
    return clientCountry.toLowerCase() === "id";
  }

  get resolveFormatCountry() {
    // Inherit from parent (entry files) — or fallback to auth
    const parentResolve = (this.$parent as any)?.resolveFormatCountry;
    if (parentResolve) return parentResolve;
    // Fallback: auth countryCode → ID
    const authCountry = this.accountCountryCode || "ID";
    return authCountry === "MY" ? "MY" : "ID";
  }

  get originCountryCode(): string {
    const code =
      LocationController.districtDetail?.countryCode ||
      AccountController.accountData.countryCode;
    return (code || "ID").toUpperCase();
  }

  get destinationCountryCode(): string {
    return (this.destination?.countryCode || "").toUpperCase();
  }

  // Mirrors the (currently unused) isCrossCountryRoute getter added to
  // delivery-service/index.vue in GENESIS-21766, but normalizes casing on
  // both sides — AccountController.accountData.countryCode is lower-cased
  // at the source while district/destination country codes are not, which
  // would otherwise make every domestic booking look cross-country.
  get isCrossCountryCod(): boolean {
    return this.isCrossCountryRoute && (this.isCOD || this.isDFOD);
  }

  get effectiveCurrencyCode(): string {
    if (this.isCrossCountryCod) {
      return getCurrencyCodeByCountry(this.destinationCountryCode);
    }
    return !this.accountIsNonForeign || this.isEditSttMY ? "MYR" : "IDR";
  }

  get prefixCurrency() {
    if (this.isCrossCountryCod) {
      return this.form.destinationCountry.currencySymbol;
    }
    return !this.accountIsNonForeign || this.isEditSttMY ? "RM" : "Rp";
  }

  get bookingClientForm() {
    return (
      this.bookingPosForClient ||
      this.bookingClientForClient ||
      this.bookingInternalForClient
    );
  }

  get isFormBookClient() {
    return this.formType === "client";
  }

  get isFormBookManual() {
    return this.formType === "manual";
  }

  get getAccount(): any {
    return {
      accountType: AccountController.accountData.account_type,
      cityCode: AccountController.accountData.account_type_detail
        .partnerLocation
        ? AccountController.accountData.account_type_detail.partnerLocation
            .city_code
        : "",
      accountClientId: AccountController.accountData.account_type_detail.id
        ? AccountController.accountData.account_type_detail.id
        : "",
      isCodBooking:
        AccountController.accountData.account_type_detail.partnerIsCodBooking,
      accountTypeDetail: AccountController.accountData.account_type_detail,
      isPOS: AccountController.accountData.isPosAccount
    };
  }

  get bookingRetail() {
    return this.accountData.account_type && this.formType === "manual";
  }

  get accountData() {
    return AccountController.accountData;
  }

  get isClientAccount() {
    return this.accountData.account_type === "client";
  }

  get isCOD() {
    return this.deliveryFeeType === "cod";
  }

  get isDFOD() {
    return this.deliveryFeeType === "dfod";
  }

  get isCA(): boolean {
    return isCustomerApps(this.formData?.sttShipmentId);
  }

  get bookingPosForClient() {
    return this.isFormBookClient && this.getAccount.accountType === "partner";
  }

  get bookingClientForClient() {
    return this.isFormBookManual && this.getAccount.accountType === "client";
  }

  get bookingInternalForClient() {
    return this.isFormBookClient && this.getAccount.accountType === "internal";
  }

  formatEstimationPrice(value: any) {
    if (this.isCrossCountryCod) {
      return formatInputByCountry(value, {
        type: "money",
        country: this.form.destinationCountry.code
      });
    }

    return formatInputByCountry(value, {
      type: "money",
      country: this.resolveFormatCountry
    });
  }

  formatRupiah(value: any) {
    return formatInputMoney(value.replace(/[\D]+/g, ""));
  }

  formatRinggit(value: any) {
    return formatInputMoneyWithDecimal(value);
  }

  formatThresholdPrice(value: number) {
    if (this.isCrossCountryCod) {
      const formatted = formatByCountry(value, {
        type: "weight",
        country: this.form.destinationCountry.code,
        fractionDigits: 6,
        minFractionDigits: 0
      });

      return `${this.prefixCurrency}${formatted}`;
    }

    return this.accountIsNonForeign || this.isEditSttMY
      ? this.formatRupiah(String(value))
      : String(value);
  }

  get placeholderItemPrice() {
    let placeholder = this.$t("bookingShipment.placeholder.itemPrice");

    if (this.bookingClientForm) {
      placeholder = "e.g 1";
    }

    return placeholder;
  }

  inputEstimationPrice(value: string) {
    this.fullFilledCOD(value, true, false);
    this.onValidateEstimationPrice(value);
    this.$emit("updateEstimationPrice", value);
  }

  get accountCountryCode(): string {
    return AccountController.accountData?.countryCode?.toUpperCase() || "ID";
  }

  onValidateEstimationPrice(value: string) {
    const price = parseByCountry(value, {
      country: this.isCrossCountryCod
        ? this.form.destinationCountry.code
        : this.accountCountryCode
    });

    let min = 0;
    let max = 0;

    if (this.isCrossCountryCod) {
      const { minPrice, maxPrice } = this.codConfigByDestination;

      min = Number(minPrice);
      max = Number(maxPrice);
    } else {
      const { min: minPrice, max: maxPrice } = this.minMaxConfig;

      min = minPrice;
      max = maxPrice;
    }

    const message = parseZodError(estimationPriceSchema, {
      estimationPrice: String(price),
      minEstimationPrice: min,
      maxEstimationPrice: max,
      productName: this.formData.productName,
      listCodActive: this.listCodActive,
      listDfodActive: this.listDfodActive,
      deliveryFeeType: this.deliveryFeeType,
      isFormBookClient: this.isFormBookClient
    });
    if (this.excludeEstimationValidation) {
      this.errorMandatory = false;
      this.errMsgPrice = "";
      return;
    }

    this.errorMandatory = this.errorIndex >= this.goodsPriceIndex && !!message;

    switch (message) {
      case "minEstimationPrice":
        this.errMsgPrice = this.$t("bookingV3.shipment.itemPrice.error.min", {
          price: this.formatThresholdPrice(min)
        });
        break;
      case "maxEstimationPrice":
        this.errMsgPrice = this.$t("bookingV3.shipment.itemPrice.error.max", {
          price: this.formatThresholdPrice(max)
        });
        break;
      case "errorCod":
        this.errMsgPrice = this.errMsgCOD(price);
        break;
      case "errorDfod":
        this.errMsgPrice = this.errMsgDFOD(price);
        break;
      default:
        this.errMsgPrice = this.$t(message);
        break;
    }
  }

  get formData() {
    return this.form;
  }

  set formData(value: any) {
    this.$emit("update:form", value);
  }

  get isNonCod() {
    return this.form.serviceType === "non-cod";
  }

  get isInsuranceBasic() {
    return this.formData.insurance === "basic";
  }

  set isInsuranceBasic(value: boolean) {
    const insurance = value ? "basic" : "free";
    this.formData.insurance = insurance;
  }

  get insuranceCommissionPos() {
    return formatByCountry(this.insuranceData.commisionPos || 0, {
      type: "money",
      country: this.resolveFormatCountry
    });
  }

  validationGetInsurance(value: number): boolean {
    return (
      !!value &&
      (this.bookingRetail || this.isCA) &&
      this.accountData.isPosAccount
    );
  }

  get isShipmentCOD() {
    return (
      this.formData.shipmentId && this.formData.isCOD && !this.formData.isDFOD
    );
  }

  get isShipmentFavorite() {
    return (
      this.formData.shipmentId && this.formData.shipmentId.match(/^AP|^AS/gi)
    );
  }

  get isCodAmount() {
    return this.isClientAccount
      ? AccountController.accountData.account_type_detail.codConfigAmount ===
          "goods_price"
      : (this.isFormBookClient &&
          this.formData.clientName?.clientCodConfigAmountData &&
          this.formData.clientName?.clientCodConfigAmountData ===
            "goods_price") ||
          this.formData.clientName?.clientCodConfigAmount === "goods_price";
  }

  get isShowCODAmountField() {
    return (
      this.isShipmentCOD || (this.isCodAmount && this.deliveryFeeType === "cod")
    );
  }

  get deliveryFeeType() {
    if (this.formData.isCOD && this.formData.isDFOD) return "dfod";
    if (this.formData.isCOD) return "cod";
    return "non-cod";
  }

  get minMaxConfig() {
    return getMinMaxGoodsPriceFromFlag(this.schemaGoodsPriceType);
  }

  get disabledBasic() {
    return (
      ((this.listCodActive.ccoIsInsurance && this.deliveryFeeType === "cod") ||
        (this.listDfodActive.ccoIsInsurance &&
          this.deliveryFeeType === "dfod")) &&
      this.formType === "manual" &&
      this.accountData.account_type !== "client"
    );
  }

  get isSetCodAmount() {
    return this.isCodAmount && this.isCOD;
  }

  get isBookingTypeShipment() {
    return this.bookingType === "shipment";
  }

  fullFilledCOD(price: any, isFromInput: boolean, isProductNameChange = false) {
    if (
      !isProductNameChange && this.isShipmentFavorite
        ? true
        : !this.isBookingShipment
    ) {
      this.formData.codAmount = price;
    }
    if (this.isCrossCountryCod || this.accountIsNonForeign) {
      price = +price.replace(/\./g, "");
    } else {
      price = +price.replace(/\./g, "").replace(/,/g, ".");
    }
    if (this.deliveryFeeType === "cod") {
      this.validationCOD(price, isFromInput);
      this.formData.isCOD = true;
      this.formData.isDFOD = false;
      return;
    } else if (this.deliveryFeeType === "dfod") {
      this.validationDFOD(price, isFromInput);
      this.formData.isCOD = true;
      this.formData.isDFOD = true;
      return;
    }
    this.formData.isCOD = false;
    this.formData.isDFOD = false;
    this.automaticSetCheckbox(price);
  }

  isAboveFiveMil = false;

  automaticSetCheckbox(price: any) {
    if (this.isCrossCountryCod) {
      this.formData.insurance = "free";
      this.isInsuranceBasic = false;
      this.disabledInsuranceBasic = true;
      this.shippingIsProtected = true;
      return;
    }

    if (this.formData.productName.includes("OTOPACK")) {
      this.isInsuranceBasic = true;
      this.disabledInsuranceBasic = true;
      this.shippingIsProtected = true;
      this.formData.insurance = "basic";
    } else {
      const validationGreather5Million =
        price >= 5000000 &&
        (this.bookingType !== "shipment" || this.isShipmentFavorite);

      if (validationGreather5Million && !this.disabledBasic) {
        this.isInsuranceBasic = true;
        this.disabledInsuranceBasic = true;
        this.shippingIsProtected = false;
        this.formData.insurance = "basic";
        this.isAboveFiveMil = true;
      } else {
        this.disabledInsuranceBasic = this.disabledBasic;
        this.shippingIsProtected = this.disabledBasic;
        if (this.isAboveFiveMil) {
          this.isInsuranceBasic = false;
          this.isAboveFiveMil = false;
        }
      }
    }
  }

  errMsgCOD(price: any) {
    const currency = this.accountIsNonForeign ? "IDR" : "MYR";
    return price > this.listCodActive.ccoMaxPrice
      ? `Harga barang COD maksimum ${formatByCountry(
          this.listCodActive.ccoMaxPrice,
          { type: "money", country: this.resolveFormatCountry }
        )}, cek & ubah lagi.`
      : `Harga barang COD minimum ${formatByCountry(
          this.listCodActive.ccoMinPrice,
          { type: "money", country: this.resolveFormatCountry }
        )}, cek & ubah lagi.`;
  }

  errMsgDFOD(price: any) {
    const currency = this.accountIsNonForeign ? "IDR" : "MYR";
    return price > this.listDfodActive.ccoMaxPrice
      ? `Harga barang DFOD maksimum ${formatByCountry(
          this.listDfodActive.ccoMaxPrice,
          { type: "money", country: this.resolveFormatCountry }
        )}, cek & ubah lagi.`
      : `Harga barang DFOD minimum ${formatByCountry(
          this.listDfodActive.ccoMinPrice,
          { type: "money", country: this.resolveFormatCountry }
        )}, cek & ubah lagi.`;
  }

  isNoActiveInsuranceAndNonClient(ccoIsInsurance: any) {
    return !ccoIsInsurance && !this.isClientAccount;
  }

  validationCOD(price: any, isFromInput: boolean) {
    // insurance tidak aktif dan yang boleh akun selain client
    if (
      !this.isNoActiveInsuranceAndNonClient(this.listCodActive.ccoIsInsurance)
    ) {
      this.disabledInsuranceBasic = true;
      this.shippingIsProtected = true;
      // auto centang
    }
    // insurance aktif auto centang hanya untuk retail
    this.automaticSetCheckbox(price);
  }

  validationDFOD(price: any, isFromInput: boolean) {
    // insurance tidak aktif dan yang boleh akun selain client
    if (
      !this.isNoActiveInsuranceAndNonClient(this.listDfodActive.ccoIsInsurance)
    ) {
      this.disabledInsuranceBasic = true;
      this.shippingIsProtected = true;
    }
    // insurance aktif auto centang hanya untuk retail
    this.automaticSetCheckbox(price);
  }

  get checkIsValidateEstimationPrice() {
    return (
      !this.isRebookingFlow &&
      (this.formData.productName.includes("OTOPACK") || this.errMsgPrice)
    );
  }

  onChangeDeliveryFeeType(isProductNameChange = false) {
    this.handleDeliveryFeeType();
    this.fullFilledCOD(
      this.formData.estimationPrice,
      false,
      isProductNameChange
    );
    if (this.checkIsValidateEstimationPrice)
      this.onValidateEstimationPrice(this.formData.estimationPrice);
  }

  get errorIndex() {
    return this.currentIndex;
  }

  set errorIndex(value) {
    this.$emit("update:currentIndex", value);
  }

  get isErrorIndex() {
    if (validationCurrentIndex(this.goodsPriceIndex, this.errorIndex)) {
      this.onValidateEstimationPrice(this.formData.estimationPrice);
      return this.errorMandatory;
    }
    return false;
  }

  handleDeliveryFeeType() {
    if (this.deliveryFeeType === "cod" || this.deliveryFeeType === "dfod") {
      this.fetchListConfigurationCOD();
    }
  }

  fetchListConfigurationCOD() {
    if (this.disabledBasic) {
      this.isInsuranceBasic = !this.disabledBasic;
      this.disabledInsuranceBasic = this.disabledBasic;
      this.shippingIsProtected = this.disabledBasic;
      this.form.insurance = "free";
    }
  }

  formatPrice(value: number) {
    return formatByCountry(value, {
      type: "money",
      country: this.accountCountryCode,
      prefix: this.accountCountryCode?.toUpperCase() === "MY" ? "RM" : "Rp"
    });
  }

  get isCoveredInsuranceBasic() {
    const goodsValue = parseByCountry(this.form.estimationPrice, {
      country: this.originCountry
    });

    const isCovered =
      this.insuranceBasic.isAvailable &&
      goodsValue > this.insuranceBasic.insuranceBasicStartFrom &&
      this.isNonCod;

    this.isInsuranceBasic = isCovered;

    return isCovered;
  }

  get originCountry() {
    return (
      LocationController.districtDetail?.countryCode ??
      AccountController.accountData.countryCode
    );
  }

  get accountIsForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  // exchange rate cod amount
  async getExchangeRate() {
    if (!this.isCrossCountryCod) return;

    await ExchangeRateController._onGetList({
      from: this.form.destinationCountry.currency,
      to: this.form.originCountry.currency,
      status: "active",
      page: 1,
      limit: 1
    });

    this.configExchangeRate = new ExchangeRateData(
      ExchangeRateController.exchangeRateData.data?.[0]
    );
  }

  configExchangeRate = new ExchangeRateData();

  get estimatedConvertedCodAmount(): string {
    const codAmount = this.formData.codAmount;

    if (!this.configExchangeRate.id || !codAmount) {
      return "";
    }

    const rate = this.configExchangeRate.exchangeRate;

    const total = codAmount * rate;

    return this.$t("bookingV4.protection.codAmount.footerEstimated", {
      currency: this.form.originCountry.currencySymbol,
      amount: formatByCountry(total, {
        type: "money",
        country: this.form.originCountry.code,
        fractionDigits: 6,
        minFractionDigits: 0
      })
    }) as string;
  }

  // cod international config
  configCodInternationalClient = new DetailCodConfigurationRetail();

  async getCodConfig() {
    const res = await CodConfigurationController.getList(
      new ApiRequestList({
        page: 1,
        limit: 20,
        status: "active"
      })
    );

    const codConfig = new DetailCodConfigurationRetail(
      res.data.find(
        (config: DetailCodConfigurationRetail) =>
          config.ccoName === "COD International Client"
      )
    );

    this.configCodInternationalClient = codConfig;
    if (this.isEdit && this.form.estimationPrice) {
      this.onValidateEstimationPrice(this.form.estimationPrice);
    }
  }

  get codConfigByDestination() {
    return new CodInternationalCountry(
      this.configCodInternationalClient.countries.find(
        (c) => c.countryCode === this.form.destinationCountry.code
      )
    );
  }

  formatPriceTooltip(value: number) {
    const num = Number(value || 0);
    const hasDecimal = !Number.isInteger(num);
    const country = this.resolveFormatCountry || this.accountCountryCode;
    return formatByCountry(num, {
      type: "money",
      country,
      prefix: country?.toUpperCase() === "MY" ? "RM" : "Rp",
      fractionDigits: hasDecimal ? 2 : 0
    });
  }
}

export default ProductPriceInsurance;
